/**
 * Complete the Look Widget — Pitchers Only
 * Runs on Shopify product pages. Loads rules from ctl-rules.json (theme asset),
 * evaluates the current product, and renders a single recommended product
 * with checkbox + size selector. Intercepts the add-to-cart form to bundle
 * both products into one cart/add.js POST.
 *
 * IIFE — no module, no dependencies. Runs in Shopify theme context.
 *
 * 5-YEAR SALE (Chris, 2026-09-07): the widget shows on sale PDPs and may
 * offer a sale product, but a 5yr- tagged pick never gets the widget's
 * percentage on top of its sale price. isSalePick() decides (PO_CTL "sale"
 * flag from Liquid, or the product's own tags from /products/<handle>.js so
 * the legacy ctl-rules.json path is covered too). A sale pick renders at its
 * sale price with the compare-at struck through and is added WITHOUT a
 * _po_discount property. The widget-pricing Function enforces the same rule
 * at checkout from the product tags.
 */
(function () {
  'use strict';

  // ── Config ──────────────────────────────────────────────────────────────────
  var ANALYTICS_URL = 'https://dashboard-production-d03c.up.railway.app';
  var DEBUG = false;   // was true — every live PDP was console-logging [PO-CTL] diagnostics

  // ── Runtime state ───────────────────────────────────────────────────────────
  var rules = null;
  var widgetId = null;

  // ── Logging ─────────────────────────────────────────────────────────────────
  function dbg() {
    if (DEBUG) console.log.apply(console, ['[PO-CTL]'].concat(Array.prototype.slice.call(arguments)));
  }

  // ── Pre-order detection ─────────────────────────────────────────────────────
  // True if /products/<handle>.js JSON shows the product is on a pre-order
  // selling-plan group. Convention from memory/project_preorders.md: the group
  // name always starts with "Preorder:" (same signal Shopify Flow uses to tag
  // pre-orders post-checkout). Without this, a CTL rule with a collection
  // output can surface a pre-order product (e.g. Shove Day Bag from
  // best-sellers) on a totally unrelated product page.
  function isPreorderProduct(p) {
    if (!p) return false;
    var groups = p.selling_plan_groups || [];
    for (var i = 0; i < groups.length; i++) {
      var name = (groups[i] && groups[i].name) || '';
      if (name.indexOf('Preorder:') === 0) return true;
    }
    return false;
  }

  // ── Sale pick detection (5-Year Sale) ───────────────────────────────────────
  // True when this pick must NOT get the widget discount: the Liquid bridge
  // flagged its handle (PO_CTL curated/auto "sale": true) or the product JSON
  // carries a tag starting with "5yr-". Tags come from /products/<handle>.js,
  // so this also covers picks that arrived via the ctl-rules.json fallback.
  function isSalePick(product, rule) {
    if (!product) return false;
    if (rule && rule.sale_handles && product.handle && rule.sale_handles[product.handle]) return true;
    var tags = product.tags || [];
    for (var i = 0; i < tags.length; i++) {
      if (String(tags[i]).indexOf('5yr-') === 0) return true;
    }
    return false;
  }

  // ── Slugify a Shopify product title to its handle ───────────────────────────
  // Mirrors Shopify's default title-to-handle convention: lowercase, drop
  // accents/punctuation, collapse runs of non-alphanumerics into single hyphens.
  function slugify(title) {
    if (!title) return '';
    return String(title)
      .toLowerCase()
      .normalize('NFKD').replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // ============================================================
  // 1. INIT — resolve rules URL from own script tag src
  // ============================================================
  function init() {
    // ---- Shopify metafields first -------------------------------------------
    // snippets/po-upsell-widgets.liquid emits window.PO_CTL from the product's
    // cro.ctl_curated / cro.ctl_auto metafields. That half of the migration off
    // ctl-rules.json shipped; this consumer never did, so the metafields were
    // being written into the page and read by nobody.
    //
    // curated = hand-pins and always win; auto = drop-generated baseline.
    // The legacy ctl-rules.json fetch below is left intact as the fallback, so
    // the products still driven by the dashboard keep working untouched.
    if (readMetafieldCTL()) return;

    var rulesUrl = null;

    // Find this script tag by looking for complete-look.js in src
    var scripts = document.querySelectorAll('script[src]');
    for (var i = 0; i < scripts.length; i++) {
      var src = scripts[i].src || '';
      if (src.indexOf('complete-look.js') !== -1) {
        rulesUrl = src.replace('complete-look.js', 'ctl-rules.json');
        break;
      }
    }

    if (!rulesUrl) {
      // Fallback: check for data attribute or use assets path
      rulesUrl = '/assets/ctl-rules.json';
    }

    dbg('Rules URL:', rulesUrl);
    loadRules(rulesUrl);
  }

  // ============================================================
  // 1B. METAFIELD SOURCE — window.PO_CTL
  // Returns true when it took ownership of rendering, so init() can stop.
  // ============================================================
  function readMetafieldCTL() {
    var meta = window.PO_CTL;
    if (!meta) return false;

    var picks = (meta.curated && meta.curated.length) ? meta.curated
              : (meta.auto && meta.auto.length) ? meta.auto
              : null;
    if (!picks) {
      dbg('PO_CTL present but empty — falling back to ctl-rules.json');
      return false;
    }

    var product = getPageProduct();
    if (!product) {
      dbg('PO_CTL present but not a product page — exiting');
      return true;
    }

    // Never pin the page's own product to itself. The Liquid already filters
    // this, but a stale metafield could still carry it.
    var handles = [], titles = [], saleHandles = {};
    for (var i = 0; i < picks.length; i++) {
      var p = picks[i];
      if (!p || !p.handle) continue;
      if (product.handle && p.handle === product.handle) continue;
      handles.push(p.handle);
      titles.push(p.title || '');
      if (p.sale) saleHandles[p.handle] = true;
    }
    if (!handles.length) {
      dbg('PO_CTL had no usable handles — falling back');
      return false;
    }

    widgetId = meta.widget_id || 'ctl';
    var rule = {
      id: 'ctl-metafield',
      rule_type: 'product',
      discount_percent: (meta.discount_percent != null ? meta.discount_percent : 10),
      output_product_handles: handles,
      output_product_titles: titles,
      sale_handles: saleHandles
    };
    dbg('CTL from metafields:', handles.join(', '));
    fetchAndRender(rule);
    return true;
  }

  // ============================================================
  // 2. LOAD RULES
  // ============================================================
  function loadRules(url) {
    // Cache buster so we always get the freshest rules
    var bustUrl = url + '?v=' + Date.now();

    fetch(bustUrl, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        rules = data.rules || [];
        widgetId = data.widget_id || 'ctl-widget';
        dbg('Loaded', rules.length, 'rule(s), widgetId:', widgetId);
        var product = getPageProduct();
        if (!product) {
          dbg('Not a product page — exiting');
          return;
        }
        evaluateAndRender(product);
      })
      .catch(function (err) {
        dbg('Failed to load rules:', err);
      });
  }

  // ============================================================
  // 3. GET PAGE PRODUCT from Shopify global
  // ============================================================
  function getPageProduct() {
    // Shopify exposes meta.product on product pages
    if (window.meta && window.meta.product) {
      return window.meta.product;
    }
    // Fallback: ShopifyAnalytics
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
      return window.ShopifyAnalytics.meta.product;
    }
    // Fallback: check URL pattern
    if (window.location.pathname.indexOf('/products/') !== -1) {
      // Return minimal object — evaluation will rely on URL handle
      var handle = window.location.pathname.split('/products/')[1].split('?')[0].split('/')[0];
      return { id: null, handle: handle, type: null };
    }
    return null;
  }

  // ============================================================
  // 4. EVALUATE RULES & RENDER
  // ============================================================
  function evaluateAndRender(product) {
    if (!rules || rules.length === 0) {
      dbg('No rules to evaluate');
      return;
    }

    for (var i = 0; i < rules.length; i++) {
      var rule = rules[i];

      // Rules JSON is pre-filtered to enabled-only at publish time
      // (publish.py SQL query has .filter_by(enabled=True)).
      // Rule schema (from models.py to_publish_dict): rule_type, trigger_product_id,
      // trigger_product_title, trigger_collection, output_product_ids,
      // output_product_titles, output_collection, discount_percent, exit_if_matched.
      var matched = false;

      if (rule.rule_type === 'product') {
        if (product.id && rule.trigger_product_id) {
          matched = String(product.id) === String(rule.trigger_product_id);
        }
        if (!matched && product.title && rule.trigger_product_title) {
          matched = product.title === rule.trigger_product_title;
        }
        if (!matched && product.handle && rule.trigger_product_title) {
          matched = product.handle === slugify(rule.trigger_product_title);
        }
      } else if (rule.rule_type === 'collection') {
        matched = productInCollection(product, rule.trigger_collection);
      }

      if (matched) {
        dbg('Rule matched:', rule.id || i);
        fetchAndRender(rule);
        return; // Only render one widget
      }
    }
  }

  // ============================================================
  // 5A. PRODUCT RULE — fetch output product by handle
  // ============================================================
  function fetchAndRender(rule) {
    // Rule schema (from publish.py): output_product_handles[] (preferred), output_product_titles[],
    // and output_product_ids[]. Prefer handles when present so we never have to slugify a title
    // that doesn't match the actual product handle (silent 404 on rename).
    var handles = rule.output_product_handles || [];
    var titles = rule.output_product_titles || [];

    if (handles.length > 0 && handles[0]) {
      // Try the pins IN ORDER and render the first one a customer can actually buy.
      // Previously this took handles[0] and stopped, with no availability check, so
      // a sold-out slot-0 pin rendered a Complete the Look whose dropdown was
      // entirely disabled options — a visible upsell that cannot be added — while
      // a perfectly buyable slot-1 pin sat unused.
      fetchFirstBuyable(handles, rule);
    } else if (titles.length > 0) {
      var handle = slugify(titles[0]);
      if (!handle) {
        dbg('Could not slugify output title:', titles[0]);
        return;
      }
      fetchProductByHandle(handle, rule);
    } else if (rule.output_collection) {
      fetchCollectionAndRender(rule, getPageProduct());
    } else {
      dbg('Rule has no output handles, titles, or collection — skipping');
    }
  }

  // Fetch every pinned handle, then render the first that is both non-pre-order and
  // has at least one purchasable variant. Falls back to the first non-pre-order
  // product if none are buyable, so behaviour degrades to the old output rather
  // than rendering nothing at all.
  function fetchFirstBuyable(handles, rule) {
    var wanted = handles.filter(Boolean).slice(0, 6);
    Promise.all(wanted.map(function (h) {
      return fetch('/products/' + h + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    })).then(function (all) {
      var usable = all.filter(function (p) { return p && !isPreorderProduct(p); });
      var pick = null;
      for (var i = 0; i < usable.length; i++) {
        var vs = usable[i].variants || [];
        for (var j = 0; j < vs.length; j++) {
          if (vs[j] && vs[j].available) { pick = usable[i]; break; }
        }
        if (pick) break;
      }
      if (!pick) {
        dbg('No pinned product is purchasable:', wanted.join(', '));
        return;   // better to show nothing than an upsell nobody can add
      }
      dbg('CTL rendering:', pick.handle);
      renderWidget(pick, rule);
    });
  }

  function fetchProductByHandle(handle, rule) {
    fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (product) {
        dbg('Fetched product:', product.title);
        if (isPreorderProduct(product)) {
          dbg('Skipping pre-order product:', product.handle);
          return;
        }
        renderWidget(product, rule);
      })
      .catch(function (err) {
        dbg('Failed to fetch product', handle, ':', err);
      });
  }

  // ============================================================
  // 5B. COLLECTION RULE — fetch products, exclude current
  // ============================================================
  function fetchCollectionAndRender(rule, currentProduct) {
    var collHandle = rule.trigger_collection || rule.output_collection;
    if (!collHandle) return;

    fetch('/collections/' + collHandle + '/products.json?limit=10', { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        var products = data.products || [];
        // Exclude current page product. /collections/X/products.json does NOT
        // include selling_plan_groups, so we have to re-fetch each candidate
        // via /products/<handle>.js to check pre-order status before picking
        // one to render.
        var currentId = currentProduct && currentProduct.id ? String(currentProduct.id) : null;
        var currentHandle = currentProduct && currentProduct.handle ? currentProduct.handle : null;
        var candidates = products.filter(function (p) {
          if (currentId && String(p.id) === currentId) return false;
          if (currentHandle && p.handle === currentHandle) return false;
          return !!p.handle;
        });
        if (candidates.length === 0) {
          dbg('No products available from collection after filtering');
          return;
        }
        var promises = candidates.map(function (p) {
          return fetch('/products/' + p.handle + '.js', { credentials: 'same-origin' })
            .then(function (r) { return r.ok ? r.json() : null; })
            .catch(function () { return null; });
        });
        Promise.all(promises).then(function (full) {
          var first = null;
          for (var i = 0; i < full.length; i++) {
            if (full[i] && !isPreorderProduct(full[i])) { first = full[i]; break; }
          }
          if (!first) {
            dbg('All collection candidates were pre-orders — nothing to render');
            return;
          }
          renderWidget(first, rule);
        });
      })
      .catch(function (err) {
        dbg('Failed to fetch collection', collHandle, ':', err);
      });
  }

  // ============================================================
  // 6. RENDER WIDGET
  // ============================================================
  function renderWidget(product, rule) {
    // Don't render if widget already exists
    if (document.querySelector('.po-ctl-widget')) {
      dbg('Widget already rendered — skipping');
      return;
    }

    // A 5yr- (sale) pick gets no widget percentage: it is sold at its sale
    // price, and interceptAddToCart() stamps no _po_discount for it.
    var onSale = isSalePick(product, rule);
    var discountPct = onSale ? 0 : (rule.discount_percent || 0);

    // Show EVERY variant. This used to filter to v.available first, which made
    // the sold-out branch of the option builder below unreachable: a sleeve with
    // two of three sizes gone rendered a dropdown containing one size, so the
    // shopper never learned the other sizes existed at all. The builder already
    // disables sold-out options and appends " — Sold Out"; let it do its job.
    // hasMultipleVariants is counted off the unfiltered list, so the two agree now.
    var variants = (product.variants || []).slice();

    // Price off the first BUYABLE variant so a sold-out first size cannot set
    // the displayed price; fall back to the first variant when none are buyable.
    var firstVariant = null;
    for (var fv = 0; fv < variants.length; fv++) {
      if (variants[fv] && variants[fv].available) { firstVariant = variants[fv]; break; }
    }
    if (!firstVariant) firstVariant = variants[0] || product.variants[0];
    var originalPriceCents = firstVariant ? firstVariant.price : (product.price || 0);
    // Show exactly what checkout charges (Chris 2026-09-27). The widget-pricing
    // Function takes an exact percent and Shopify rounds each item's discount
    // DOWN to the cent (measured at checkout 9/27), so $40 at 10% is $36.00.
    // The old ".99" round-up showed $36.99 for a $36.00 charge.
    var salePriceCents = discountPct > 0
      ? originalPriceCents - Math.floor(originalPriceCents * discountPct / 100)
      : originalPriceCents;
    // With no widget markdown, show the product's own compare-at (the sale
    // price already struck on the PDP header) so the card matches the page.
    var compareAtCents = firstVariant && firstVariant.compare_at_price
      ? parseInt(firstVariant.compare_at_price, 10) || 0
      : (parseInt(product.compare_at_price, 10) || 0);
    var struckPriceCents = discountPct > 0
      ? originalPriceCents
      : (compareAtCents > originalPriceCents ? compareAtCents : 0);

    // Image source — coerce to string and request a higher-density Shopify
    // CDN size for sharp display at 60px CSS on retina.
    var imgSrc = '';
    if (typeof product.featured_image === 'string') {
      imgSrc = product.featured_image;
    } else if (product.featured_image && product.featured_image.src) {
      imgSrc = product.featured_image.src;
    } else if (product.images && product.images.length > 0) {
      var first = product.images[0];
      if (typeof first === 'string') imgSrc = first;
      else if (first && first.src) imgSrc = first.src;
    }
    function shopifySize(url, size) {
      if (!url || typeof url !== 'string') return '';
      return url.replace(/(\.[a-z]+)(\?|$)/i, '_' + size + 'x' + size + '$1$2');
    }

    // ── Build DOM ────────────────────────────────────────────────────────────
    var widget = document.createElement('div');
    widget.className = 'po-ctl-widget';
    widget.setAttribute('data-widget-id', widgetId);
    widget.setAttribute('data-rule-id', rule.id || '');

    var heading = document.createElement('div');
    heading.className = 'po-ctl-heading';
    heading.textContent = 'Complete the Look';

    var row = document.createElement('div');
    row.className = 'po-ctl-product';

    // Checkbox — default unchecked. Customer must opt-in by picking a size,
    // which auto-checks the box. Prevents accidental wrong-size adds.
    var checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'po-ctl-checkbox';
    checkbox.checked = false;
    checkbox.setAttribute('aria-label', 'Add ' + product.title + ' to cart');

    // Image
    var img = document.createElement('img');
    img.className = 'po-ctl-image';
    img.src = shopifySize(imgSrc, 360);
    img.srcset = imgSrc
      ? (shopifySize(imgSrc, 240) + ' 1x, ' + shopifySize(imgSrc, 360) + ' 2x, ' + shopifySize(imgSrc, 540) + ' 3x')
      : '';
    img.width = 130;
    img.height = 130;
    img.alt = product.title;
    img.loading = 'lazy';

    // Info column
    var info = document.createElement('div');
    info.className = 'po-ctl-info';

    var title = document.createElement('div');
    title.className = 'po-ctl-title';
    title.textContent = product.title;

    var prices = document.createElement('div');
    prices.className = 'po-ctl-prices';

    var saleEl = document.createElement('span');
    saleEl.className = 'po-ctl-sale-price';
    saleEl.textContent = formatMoney(salePriceCents);
    prices.appendChild(saleEl);

    if (struckPriceCents > 0) {
      var origEl = document.createElement('span');
      origEl.className = 'po-ctl-original-price';
      origEl.textContent = formatMoney(struckPriceCents);
      prices.appendChild(origEl);
    }
    if (onSale) widget.setAttribute('data-po-sale', '1');

    // Single-variant products (e.g. accessories with no size choice) skip the
    // size dropdown entirely. The customer just toggles the checkbox.
    var hasMultipleVariants = (product.variants || []).length > 1;
    // Name the thing being chosen instead of always saying "size". Not every
    // CTL product varies by size — the rosin bag varies by printed design, so
    // "Select a size" was asking for something the dropdown does not contain.
    // Read the product's own first option name, and map the few cases where the
    // merchandising word differs from the Shopify option name. Anything
    // size-shaped, or a product with no usable option name, keeps the original
    // wording so apparel is untouched.
    var OPTION_WORDS = { style: 'design' };
    var optName = (product.options && product.options.length)
      ? String(typeof product.options[0] === 'object' ? product.options[0].name : product.options[0]).trim()
      : '';
    var optWord = OPTION_WORDS[optName.toLowerCase()] || optName.toLowerCase();
    var chooseWhat = (!optWord || /size/.test(optWord)) ? 'a size' : optWord;

    var select = document.createElement('select');
    select.className = 'po-ctl-size-select';
    select.setAttribute('aria-label', 'Select ' + chooseWhat + ' for ' + product.title);

    if (hasMultipleVariants) {
      var placeholderOpt = document.createElement('option');
      placeholderOpt.value = '';
      placeholderOpt.textContent = 'Select ' + chooseWhat;
      placeholderOpt.disabled = true;
      placeholderOpt.selected = true;
      select.appendChild(placeholderOpt);

      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var opt = document.createElement('option');
        opt.value = v.id;
        opt.textContent = v.title;
        if (!v.available) {
          opt.disabled = true;
          opt.textContent += ' — Sold Out';
        }
        select.appendChild(opt);
      }

      // Auto-check the add-on checkbox the moment a size is chosen, and uncheck
      // if the customer reverts to the placeholder. Keeps the two controls in
      // sync so a checked box always implies a real size.
      //
      // Also swap the thumbnail to the chosen variant's own photo. Not every
      // product varies by size — the rosin bag is ONE product holding seven
      // different printed sacks, so the generic featured image is the wrong
      // picture for six of the seven choices. Falls back to the product image
      // when a variant has none of its own (sleeves, where the variants are
      // sizes and every one looks identical).
      select.addEventListener('change', function () {
        checkbox.checked = !!select.value;
        if (!img) return;
        var picked = null;
        for (var vi = 0; vi < variants.length; vi++) {
          if (String(variants[vi].id) === String(select.value)) { picked = variants[vi]; break; }
        }
        var vfi = picked && picked.featured_image;
        var vSrc = '';
        if (typeof vfi === 'string') vSrc = vfi;
        else if (vfi && vfi.src) vSrc = vfi.src;
        if (!vSrc) vSrc = imgSrc;
        if (!vSrc) return;
        img.src = shopifySize(vSrc, 360);
        img.srcset = shopifySize(vSrc, 240) + ' 1x, ' +
                     shopifySize(vSrc, 360) + ' 2x, ' +
                     shopifySize(vSrc, 540) + ' 3x';
        img.alt = picked && picked.title ? product.title + ' — ' + picked.title : product.title;
      });
    } else {
      // Pre-select the one variant so the existing intercept code can read
      // sizeSelect.value without rendering the dropdown.
      select.style.display = 'none';
      if (firstVariant) {
        var soloOpt = document.createElement('option');
        soloOpt.value = firstVariant.id;
        soloOpt.selected = true;
        select.appendChild(soloOpt);
      }
    }

    info.appendChild(title);
    info.appendChild(prices);
    info.appendChild(select);

    row.appendChild(checkbox);
    if (imgSrc) row.appendChild(img);
    row.appendChild(info);

    widget.appendChild(heading);
    widget.appendChild(row);

    // ── Quick view (Chris 2026-10-07, Vuori pattern) ─────────────────────────
    // Tapping the title or the photo opens a sheet on THIS page instead of
    // linking out, because the product's own page shows the full price and the
    // shopper leaves the item they came for. The sheet shows the same price as
    // the card (bundle price, labeled), every photo, and the sizes. Its button
    // only picks the size and ticks the box; the main Add to Cart still adds
    // both, through interceptAddToCart() below, so nothing about pricing moves.
    var openQuickView = function (e) {
      if (e) { e.preventDefault(); e.stopPropagation(); }
      openSheet({
        product: product, variants: variants, select: select, checkbox: checkbox,
        discountPct: discountPct, onSale: onSale, chooseWhat: chooseWhat,
        optName: optName, hasMultipleVariants: hasMultipleVariants, rule: rule
      });
    };
    [img, title].forEach(function (el) {
      el.classList.add('po-ctl-open');
      el.setAttribute('role', 'button');
      el.setAttribute('tabindex', '0');
      el.setAttribute('aria-label', 'See photos and sizes for ' + product.title);
      el.addEventListener('click', openQuickView);
      el.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') openQuickView(e);
      });
    });

    // ── Insert into page ─────────────────────────────────────────────────────
    var inserted = insertWidget(widget);
    if (!inserted) {
      dbg('Could not find insertion point — widget not rendered');
      return;
    }

    dbg('Widget rendered for:', product.title);
    whenSeen(widget, function () {
      trackEvent('impression', rule, matchMeta(product));
    });

    // ── Intercept add-to-cart ────────────────────────────────────────────────
    interceptAddToCart(product, rule, discountPct, select, checkbox);
  }

  // ============================================================
  // 6B. QUICK VIEW SHEET
  // ============================================================
  // Bottom sheet on phones, centered panel on desktop. Built once per open and
  // removed on close, so it holds no state the card does not already hold: the
  // card's <select> and checkbox stay the single source of truth.
  function imgUrl(src, width) {
    if (!src || typeof src !== 'string') return '';
    if (src.indexOf('//') === 0) src = 'https:' + src;
    return src.replace(/(\.[a-z]+)(\?|$)/i, '_' + width + 'x$1$2');
  }

  function srcOf(x) {
    if (!x) return '';
    if (typeof x === 'string') return x;
    return x.src || '';
  }

  function variantPrices(v, ctx) {
    var price = v ? v.price : 0;
    var now = ctx.discountPct > 0 ? price - Math.floor(price * ctx.discountPct / 100) : price;
    var compare = v && v.compare_at_price ? parseInt(v.compare_at_price, 10) || 0 : 0;
    var was = ctx.discountPct > 0 ? price : (compare > price ? compare : 0);
    return { now: now, was: was };
  }

  function openSheet(ctx) {
    if (document.querySelector('.po-ctl-sheet')) return;
    var product = ctx.product;
    var lastFocus = document.activeElement;
    var chosen = null;
    for (var c = 0; c < ctx.variants.length; c++) {
      if (String(ctx.variants[c].id) === String(ctx.select.value)) { chosen = ctx.variants[c]; break; }
    }
    if (!ctx.hasMultipleVariants) chosen = ctx.variants[0] || null;

    var images = (product.images || []).map(srcOf).filter(Boolean);
    if (!images.length && product.featured_image) images = [srcOf(product.featured_image)];

    var root = document.createElement('div');
    root.className = 'po-ctl-sheet';
    root.innerHTML =
      '<div class="po-ctl-sheet__backdrop"></div>' +
      '<div class="po-ctl-sheet__panel" role="dialog" aria-modal="true" aria-labelledby="po-ctl-sheet-title">' +
        '<div class="po-ctl-sheet__head">' +
          '<div>' +
            '<div class="po-ctl-sheet__title" id="po-ctl-sheet-title"></div>' +
            '<div class="po-ctl-sheet__prices"><span class="po-ctl-sheet__now"></span><span class="po-ctl-sheet__was"></span></div>' +
            '<div class="po-ctl-sheet__note"></div>' +
          '</div>' +
          '<button type="button" class="po-ctl-sheet__close" aria-label="Close"><svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true"><path d="M2 2l14 14M16 2L2 16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg></button>' +
        '</div>' +
        '<div class="po-ctl-sheet__gallery"><div class="po-ctl-sheet__track"></div><div class="po-ctl-sheet__dots"></div></div>' +
        '<div class="po-ctl-sheet__body">' +
          '<div class="po-ctl-sheet__label"></div>' +
          '<div class="po-ctl-sheet__sizes"></div>' +
          '<button type="button" class="po-ctl-sheet__cta"></button>' +
          '<div class="po-ctl-sheet__hint">It goes in your cart with this item when you tap Add to Cart.</div>' +
          '<button type="button" class="po-ctl-sheet__remove">Remove from my order</button>' +
        '</div>' +
      '</div>';

    var q = function (sel) { return root.querySelector(sel); };
    q('.po-ctl-sheet__title').textContent = product.title;

    // Photos: swipe on phones (scroll-snap), dots show where you are.
    var track = q('.po-ctl-sheet__track');
    var dots = q('.po-ctl-sheet__dots');
    images.forEach(function (src, i) {
      var im = document.createElement('img');
      im.src = imgUrl(src, 800);
      im.srcset = imgUrl(src, 480) + ' 480w, ' + imgUrl(src, 800) + ' 800w, ' + imgUrl(src, 1100) + ' 1100w';
      im.sizes = '(min-width: 768px) 460px, 100vw';
      im.alt = product.title + (images.length > 1 ? ' photo ' + (i + 1) : '');
      im.loading = i === 0 ? 'eager' : 'lazy';
      track.appendChild(im);
      if (images.length > 1) {
        var d = document.createElement('button');
        d.type = 'button';
        d.className = 'po-ctl-sheet__dot' + (i === 0 ? ' is-on' : '');
        d.setAttribute('aria-label', 'Photo ' + (i + 1));
        d.addEventListener('click', function () { goTo(i); });
        dots.appendChild(d);
      }
    });
    function goTo(i) { track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' }); }
    track.addEventListener('scroll', function () {
      var i = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
      var all = dots.children;
      for (var k = 0; k < all.length; k++) all[k].classList.toggle('is-on', k === i);
    }, { passive: true });

    // Sizes (or design, for the rosin bag): one button per variant.
    var sizes = q('.po-ctl-sheet__sizes');
    var label = q('.po-ctl-sheet__label');
    if (ctx.hasMultipleVariants) {
      var word = ctx.chooseWhat === 'a size' ? 'Size' : ctx.chooseWhat.charAt(0).toUpperCase() + ctx.chooseWhat.slice(1);
      label.textContent = word;
      ctx.variants.forEach(function (v) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'po-ctl-sheet__size';
        b.textContent = v.title;
        b.setAttribute('data-id', v.id);
        if (!v.available) {
          b.disabled = true;
          b.setAttribute('aria-label', v.title + ', sold out');
        }
        b.addEventListener('click', function () {
          chosen = v;
          paint();
          var vs = srcOf(v.featured_image);
          if (vs) {
            var base = vs.split('?')[0].replace(/^https?:/, '');
            for (var ii = 0; ii < images.length; ii++) {
              if (images[ii].split('?')[0].replace(/^https?:/, '') === base) { goTo(ii); break; }
            }
          }
        });
        sizes.appendChild(b);
      });
    } else {
      label.style.display = 'none';
      sizes.style.display = 'none';
    }

    var cta = q('.po-ctl-sheet__cta');
    var remove = q('.po-ctl-sheet__remove');
    function paint() {
      var p = variantPrices(chosen || firstAvailable(ctx.variants), ctx);
      q('.po-ctl-sheet__now').textContent = formatMoney(p.now);
      q('.po-ctl-sheet__was').textContent = p.was ? formatMoney(p.was) : '';
      // Label only a real markdown. A no-discount pick (rosin is flagged like a
      // sale pick so it never gets the widget percent) has nothing struck, and
      // calling its full price a "Sale price" would be false.
      q('.po-ctl-sheet__note').textContent = ctx.discountPct > 0
        ? 'Bundle price when you add it with this item'
        : (p.was ? 'Sale price' : '');
      var btns = sizes.children;
      for (var k = 0; k < btns.length; k++) {
        btns[k].classList.toggle('is-on', !!chosen && btns[k].getAttribute('data-id') === String(chosen.id));
        btns[k].setAttribute('aria-pressed', btns[k].classList.contains('is-on') ? 'true' : 'false');
      }
      var ready = !!chosen && chosen.available;
      cta.disabled = !ready;
      cta.textContent = ready ? 'Add to my order' : 'Select ' + ctx.chooseWhat;
      remove.style.display = ctx.checkbox.checked ? '' : 'none';
    }
    paint();

    cta.addEventListener('click', function () {
      if (!chosen || !chosen.available) return;
      if (ctx.hasMultipleVariants) {
        ctx.select.value = String(chosen.id);
        // Reuse the card's own change handler: it ticks the box and swaps the photo.
        ctx.select.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        ctx.checkbox.checked = true;
      }
      trackEvent('quickview_select', ctx.rule, matchMeta(product));
      close();
    });
    remove.addEventListener('click', function () {
      ctx.checkbox.checked = false;
      close();
    });

    function onKey(e) {
      if (e.key === 'Escape') close();
    }
    function close() {
      document.removeEventListener('keydown', onKey);
      document.documentElement.classList.remove('po-ctl-sheet-lock');
      root.classList.remove('is-open');
      setTimeout(function () { if (root.parentNode) root.parentNode.removeChild(root); }, 250);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }
    q('.po-ctl-sheet__close').addEventListener('click', close);
    q('.po-ctl-sheet__backdrop').addEventListener('click', close);
    document.addEventListener('keydown', onKey);

    document.body.appendChild(root);
    document.documentElement.classList.add('po-ctl-sheet-lock');
    requestAnimationFrame(function () { root.classList.add('is-open'); });
    q('.po-ctl-sheet__close').focus({ preventScroll: true });
    trackEvent('quickview_open', ctx.rule, matchMeta(product));
  }

  function firstAvailable(variants) {
    for (var i = 0; i < variants.length; i++) if (variants[i] && variants[i].available) return variants[i];
    return variants[0] || null;
  }

  // ============================================================
  // Insert widget above the Add to Cart button / form
  // ============================================================

  // True if the element is actually rendered/visible in the layout.
  // offsetParent === null catches display:none on the element OR any ancestor
  // (the pre-order PDP case: the theme hides the standard buy-buttons block and
  // swaps in a pre-order button, so a widget inserted there inherits a hidden
  // ancestor and renders 0x0). We also allow position:fixed elements, which
  // report offsetParent === null even when visible.
  function isVisible(el) {
    if (!el) return false;
    if (el.offsetParent !== null) return true;
    var cs = window.getComputedStyle ? window.getComputedStyle(el) : null;
    if (cs && cs.position === 'fixed' && cs.display !== 'none' && cs.visibility !== 'hidden') {
      return el.offsetWidth > 0 || el.offsetHeight > 0;
    }
    return false;
  }

  function insertWidget(widget) {
    // Explicit mount point, checked before anything else. The selectors below
    // find the ATC button, walk up to its form, and insert BEFORE that form.
    // On a stock PDP that puts the widget just above the buy button, because
    // there the form wraps only the buy-button block. A template whose
    // /cart/add form wraps something larger breaks that assumption: the Shove
    // Day Bag PDP wraps its ENTIRE product grid in one form, so "before the
    // form" landed the widget above the gallery, top-left of the page. Such a
    // template declares where the widget actually belongs and we honour it.
    // querySelector returns null on every other PDP, so nothing else changes.
    var explicitMount = document.querySelector('[data-po-ctl-mount]');
    if (explicitMount && isVisible(explicitMount)) {
      explicitMount.appendChild(widget);
      if (isVisible(widget)) {
        dbg('Widget mounted to explicit [data-po-ctl-mount]');
        return true;
      }
      if (widget.parentElement) widget.parentElement.removeChild(widget);
    }

    // Try common Shopify theme selectors for the product form
    var selectors = [
      'form[action="/cart/add"] [name="add"]',   // button inside form
      'form[action*="/cart/add"] [type="submit"]',
      '.product-form__submit',
      '.btn--add-to-cart',
      '#AddToCart',
      '[data-add-to-cart]',
      'form[action="/cart/add"]'
    ];

    for (var i = 0; i < selectors.length; i++) {
      var el = document.querySelector(selectors[i]);
      if (el) {
        // Walk up to the form or a suitable container
        var form = el.closest('form[action*="/cart/add"]') || el.parentElement;
        if (form && form.parentElement) {
          form.parentElement.insertBefore(widget, form);
          // On a NORMAL PDP this anchor is visible — return immediately so
          // existing behavior is unchanged. On a pre-order PDP the standard
          // buy-buttons container is hidden (theme swaps in a pre-order button
          // and hides the original form/its wrapper), so the widget lands inside
          // a display:none ancestor and renders 0x0. Detect that and fall back
          // to a known-visible mount point below.
          if (isVisible(widget)) {
            return true;
          }
          dbg('Primary mount point is hidden — falling back to a visible anchor');
          if (widget.parentElement) widget.parentElement.removeChild(widget);
          break; // stop trying buy-button selectors; go to the fallback
        }
      }
    }

    // Fallback: mount into a reliably-visible product container. Only reached
    // when the buy-buttons anchor was missing OR was hidden (pre-order PDPs).
    // Append to the first VISIBLE container from a list of common theme
    // product-info wrappers so the widget shows in the product detail column.
    var fallbackSelectors = [
      '.product-info',          // Impact / Dawn-family product info column
      '.product__info-wrapper',
      '.product__info',
      '.product-single__meta',
      '.product__details',
      '.product-form',
      '[data-product-info]',
      'main'                    // last-ditch but always present & visible
    ];
    for (var k = 0; k < fallbackSelectors.length; k++) {
      var container = document.querySelector(fallbackSelectors[k]);
      if (container && isVisible(container)) {
        container.appendChild(widget);
        if (isVisible(widget)) {
          dbg('Widget mounted to fallback container:', fallbackSelectors[k]);
          return true;
        }
        // Container reported visible but the widget still isn't (rare); detach
        // and keep looking rather than leave an invisible widget.
        if (widget.parentElement) widget.parentElement.removeChild(widget);
      }
    }

    // Last resort: insert before the first form on the page that looks like a
    // product form (original behavior, kept for safety).
    var forms = document.querySelectorAll('form');
    for (var j = 0; j < forms.length; j++) {
      if (forms[j].action && forms[j].action.indexOf('/cart/add') !== -1 && forms[j].parentElement) {
        forms[j].parentElement.insertBefore(widget, forms[j]);
        return true;
      }
    }

    return false;
  }

  // ============================================================
  // 7. INTERCEPT ADD TO CART
  // ============================================================
  function interceptAddToCart(ctlProduct, rule, discountPct, sizeSelect, checkbox) {
    var form = document.querySelector('form[action="/cart/add"], form[action*="/cart/add"]');
    if (!form) {
      dbg('No product form found — cannot intercept add to cart');
      return;
    }

    function flagSizeMissing() {
      sizeSelect.style.outline = '2px solid #e85b2d';
      sizeSelect.style.outlineOffset = '2px';
      sizeSelect.scrollIntoView({ behavior: 'smooth', block: 'center' });
      sizeSelect.focus();
      setTimeout(function () { sizeSelect.style.outline = ''; }, 2400);
    }

    sizeSelect.addEventListener('change', function () {
      sizeSelect.style.outline = '';
    });

    // Capture phase + stopImmediatePropagation so we run before any theme
    // handler. Themes commonly bind their own AJAX submit/click on the cart
    // form, which would otherwise add the main variant a second time alongside
    // our combined cart/add.js call.
    var submitHandler = function (e) {
      if (!checkbox.checked) {
        dbg('CTL checkbox unchecked — normal add to cart');
        return;
      }

      var ctlVariantId = parseInt(sizeSelect.value, 10);
      if (!ctlVariantId) {
        // Checkbox is checked but no size picked — block the whole add. Prior
        // behavior silently dropped the upsell which surprised customers.
        dbg('CTL checkbox checked but no size — blocking submit');
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        flagSizeMissing();
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      var mainVariantInput = form.querySelector('[name="id"]');
      var mainVariantId = mainVariantInput ? parseInt(mainVariantInput.value, 10) : null;
      // Learning loop: which page (trigger) drove this CTL conversion.
      // Mirrors bundle-save.js so both widgets stamp the same shape.
      var pageProductForTrigger = getPageProduct();
      var poTrigger = pageProductForTrigger && pageProductForTrigger.id ? String(pageProductForTrigger.id) : '';
      var mainQtyInput = form.querySelector('[name="quantity"]');
      var mainQty = mainQtyInput ? parseInt(mainQtyInput.value, 10) || 1 : 1;

      if (!mainVariantId) {
        dbg('Could not find main variant ID — falling back to normal submit');
        form.removeEventListener('submit', submitHandler, true);
        form.submit();
        return;
      }

      dbg('Intercepting submit — adding main variant', mainVariantId, '+ CTL variant', ctlVariantId);

      var items = [
        { id: mainVariantId, quantity: mainQty },
        {
          id: ctlVariantId,
          quantity: 1,
          properties: {
            '_po_widget': 'ctl',
            '_po_widget_id': widgetId || '',
            '_po_rule_id': rule.id || '',
            '_po_trigger': poTrigger
          }
        }
      ];
      // Only a discounted pick carries _po_discount. A sale pick (or a 0%
      // rule) gets none, so the widget-pricing Function has nothing to parse.
      if (discountPct > 0) items[1].properties['_po_discount'] = discountPct + '%';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: items })
      })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (d) { throw new Error(d.description || 'Cart error'); });
          return r.json();
        })
        .then(function (data) {
          dbg('Items added to cart:', data);
          trackEvent('click', rule, matchMeta(ctlProduct));
          document.dispatchEvent(new CustomEvent('po:cart:added', {
            bubbles: true,
            detail: { items: data.items || [] }
          }));
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        })
        .catch(function (err) {
          dbg('Cart add failed:', err);
          form.removeEventListener('submit', submitHandler, true);
          form.submit();
        });
    };

    form.addEventListener('submit', submitHandler, true);
  }

  // ============================================================
  // 8. ANALYTICS
  // ============================================================
  // Fire once, and only when the element is genuinely on screen. This used to
  // fire the instant the widget was inserted into the DOM, so an "impression"
  // recorded that the page had rendered the widget, not that anyone saw it.
  // This widget mounts above the buy button and bundle-save mounts below it, so
  // part of the gap between their conversion rates was scroll depth rather than
  // performance. Both now count the same thing. Half the widget visible is the bar.
  //
  // Browsers without IntersectionObserver keep the old behaviour rather than
  // losing the event; that is a rounding error of traffic.
  function whenSeen(el, fn) {
    if (typeof IntersectionObserver === 'undefined') { fn(); return; }
    var fired = false;
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting && !fired) {
          fired = true;
          io.disconnect();
          fn();
        }
      }
    }, { threshold: 0.5 });
    io.observe(el);
  }

  // Which match this event belongs to. The beacon used to send only
  // {event_type, widget_id, rule_id, session_id}, and rule_id is the constant
  // 'ctl-metafield' on every metafield-sourced match — so impressions arrived
  // as one undifferentiated pile. You could see that N people were shown a CTL
  // widget, never which one. That makes the impression data useless for the
  // only question it can answer that order data cannot: what share of the
  // people who SAW a match went on to buy it.
  //
  // Same trigger id the cart line carries in _po_trigger, so the two join.
  function matchMeta(offered) {
    var pp = getPageProduct();
    return {
      trigger: pp && pp.id ? String(pp.id) : '',
      trigger_handle: pp && pp.handle ? pp.handle : '',
      offered: offered && offered.id ? String(offered.id) : '',
      offered_handle: offered && offered.handle ? offered.handle : ''
    };
  }

  function trackEvent(eventType, rule, extra) {
    if (!ANALYTICS_URL) return;
    try {
      fetch(ANALYTICS_URL + '/api/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'omit',
        body: JSON.stringify({
          event_type: eventType,
          widget_id: widgetId,
          rule_id: rule.id || null,
          session_id: getSessionId(),
          metadata: extra || {}
        })
      }).catch(function () {}); // fire-and-forget
    } catch (e) {}
  }

  // ============================================================
  // 9. SESSION ID
  // ============================================================
  function getSessionId() {
    var key = 'po_session_id';
    try {
      var existing = sessionStorage.getItem(key);
      if (existing) return existing;
      var id = 'sess_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
      sessionStorage.setItem(key, id);
      return id;
    } catch (e) {
      return 'unknown';
    }
  }

  // ============================================================
  // HELPERS
  // ============================================================
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function productInCollection(product, collectionHandle) {
    // Check Shopify's page context for collection membership
    // window.meta.page.pageType === 'collection' + window.meta.page.resourceId won't help here.
    // Best effort: check if the product handle appears in a collection JSON we already fetched,
    // or rely on rule data to include explicit product handles/IDs.
    // For now: check if page URL contains the collection handle (e.g. customer landed from collection)
    if (!collectionHandle) return false;
    var referrer = document.referrer || '';
    var url = window.location.href;
    return referrer.indexOf('/collections/' + collectionHandle) !== -1 ||
           url.indexOf('/collections/' + collectionHandle) !== -1;
  }

  // ============================================================
  // ENTRY POINT
  // ============================================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
